import FilesAPI from '../api/FilesAPI';
import CONFIG from '../config/config';

/**
 * NOTE: This utility is tailored for Commission Contracts flows.
 * - Supports both PUBLIC (brides) and AUTHENTICATED (store) uploads
 * - Uses commission-contracts domain and expects a trackingId/prefixId
 * If you need a generic uploader for other domains, create a separate, generic util.
 */

export interface FileUploadOptions {
  fileName: string;
  file: File;
  trackingId: string;
  isPublic?: boolean;
}

export interface FileUploadResult {
  url: string;
  isPublic: boolean;
}

// Decides whether to use public endpoints; default true for commission flows
const shouldUsePublicEndpoints = (isPublic?: boolean): boolean => {
  if (isPublic !== undefined) return isPublic;
  return true;
};

export const uploadFile = async (options: FileUploadOptions): Promise<FileUploadResult> => {
  const { fileName, file, trackingId, isPublic } = options;
  const usePublic = shouldUsePublicEndpoints(isPublic);
  
  const isLargeFile = (file: File): boolean => {
    const threshold = isPublic ? CONFIG.PUBLIC_HEAVY_FILE_THRESHOLD : CONFIG.HEAVY_FILE_THRESHOLD;
    return file.size > threshold * 1024 * 1024;
  };
  
  try {
    let fileUrl: string;
    
    if (isLargeFile(file)) {
      if (usePublic) {
        const uploadUrlResponse = await FilesAPI.getPublicUploadUrl(fileName, trackingId);
        const uploadUrl = uploadUrlResponse.data.url;
        await FilesAPI.uploadPublicHeavyFile(uploadUrl, file, fileName);
        fileUrl = uploadUrl;
      } else {
        const uploadUrlResponse = await FilesAPI.getUploadUrl(fileName, 'commission-contracts', trackingId);
        const uploadUrl = uploadUrlResponse.data.url;
        await FilesAPI.uploadHeavyFile(uploadUrl, file, fileName);
        fileUrl = uploadUrl;
      }
    } else {
      const formData = new FormData();
      formData.append('file', file);
      
      if (usePublic) {
        const uploadResponse = await FilesAPI.uploadPublic(fileName, formData, trackingId);
        fileUrl = uploadResponse.data.url;
      } else {
        const uploadResponse = await FilesAPI.upload(`/v1/files/commission-contracts/uploadV2?prefixId=${trackingId}`, fileName, formData);
        fileUrl = uploadResponse.data.url;
      }
    }
    
    return { url: fileUrl, isPublic: usePublic };
  } catch (error) {
    console.error('File upload failed:', error);
    throw error;
  }
};

export const getFileUrl = async (fileName: string, trackingId: string, isPublic?: boolean): Promise<string> => {
  const usePublic = shouldUsePublicEndpoints(isPublic);
  
  try {
    if (usePublic) {
      const response = await FilesAPI.getPublicUrl(fileName, trackingId);
      return response.data.url;
    } else {
      const response = await FilesAPI.getUrl(fileName, 'commission-contracts', trackingId);
      return response.data.url;
    }
  } catch (error) {
    console.error('Failed to get file URL:', error);
    throw error;
  }
};

export const uploadMultipleFiles = async (
  files: Array<{ file: File; fileName: string }>,
  trackingId: string,
  isPublic?: boolean,
  onProgress?: (current: number, total: number, fileName: string) => void
): Promise<FileUploadResult[]> => {
  const results: FileUploadResult[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const { file, fileName } = files[i];
    
    if (onProgress) onProgress(i + 1, files.length, fileName);
    
    const result = await uploadFile({ fileName, file, trackingId, isPublic });
    results.push(result);
  }
  
  return results;
};


